import type { ComponentType, JSX, PropsWithChildren } from 'react';

import { TrackingContext } from '@change-corgi/core/react/tracking';
import type { PetitionStatus } from '@change-corgi/middle-end/gql';

import { useCurrentUserId } from 'src/app/shared/hooks/session';
import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { PetitionCommentPageCombinedState, PetitionCommentPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { PetitionCommentPageDataContextProvider } from './pageData';

export type ErrorHandlerProps = { context: ErrorStateDetails<PetitionCommentPageCombinedState> };

type Props = {
	commentId: string;
	petitionSlug: string;
	prefetchedData: PetitionCommentPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export function PetitionCommentPageContextProvider({
	children,
	commentId,
	petitionSlug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}: PropsWithChildren<Props>): JSX.Element | null {
	const pageContextState = usePageContext(petitionSlug, commentId, prefetchedData);
	const userId = useCurrentUserId();

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const { pageData } = pageContextState;

	return (
		<PetitionCommentPageDataContextProvider pageData={pageData}>
			<TrackingContext
				properties={{
					petition_id: pageData.data.petition.id,
					comment_id: pageData.data.comment.id,
					likes: pageData.data.comment.likes,
					own_petition: pageData.data.petition.user.id === userId,
					petition_status: getTrackingPetitionStatus(pageData.data.petition.status),
				}}
			>
				{children}
			</TrackingContext>
		</PetitionCommentPageDataContextProvider>
	);
}

function getTrackingPetitionStatus(status: PetitionStatus) {
	switch (status) {
		case 'VICTORY':
			return 'victory';
		case 'CLOSED':
			return 'closed';
		default:
			return 'active';
	}
}
